import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listProfiles, updateProfile, deactivateProfile } from "../services/profile";
import PermissionGroupsField from "./shared/PermissionGroupsField";
import { useConfirm } from "../hook/useConfirm";

const ListaPerfisLayer = () => {
  const { t } = useTranslation();
  const { confirm, ConfirmModal } = useConfirm();

  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editPermissions, setEditPermissions] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchProfiles = () => {
    setLoading(true);
    listProfiles()
      .then((resp) => setProfiles(resp.data || []))
      .catch(() => toast.error(t("perfis.error.load")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEdit = (profile) => {
    setEditTarget(profile);
    setEditForm({ description: profile.description || "" });
    setEditPermissions(Array.isArray(profile.permissions) ? profile.permissions : []);
  };

  const closeEdit = () => {
    setEditTarget(null);
    setEditForm(null);
    setEditPermissions([]);
  };

  const handleSaveEdit = async () => {
    if (!editTarget || !editForm) return;
    setSaving(true);
    try {
      const payload = { description: editForm.description };
      if (!editTarget.isSystem) {
        payload.permissions = editPermissions;
      }
      await updateProfile(editTarget.id, payload);
      toast.success(t("perfis.success.updated"));
      closeEdit();
      fetchProfiles();
    } catch (err) {
      toast.error(t("perfis.error.save"));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (profile) => {
    if (!(await confirm(t("perfis.confirm.deactivate", { name: profile.description })))) return;
    try {
      await deactivateProfile(profile.id);
      toast.success(t("perfis.success.deactivated"));
      fetchProfiles();
    } catch (err) {
      toast.error(t("perfis.error.deactivate"));
    }
  };

  const permissionsLabel = (profile) => {
    if (!Array.isArray(profile.permissions)) return "-";
    if (profile.permissions.includes("*")) return t("perfis.permissions.all");
    return profile.permissions.length;
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='d-flex align-items-center justify-content-between mb-16'>
              <h6 className='mb-0'>{t("perfis.breadcrumb.list")}</h6>
              <Link to='/utilizadores/perfis/criar' className='btn btn-primary-600 d-flex align-items-center gap-1'>
                <Icon icon='mdi:plus' />
                {t("perfis.action.create")}
              </Link>
            </div>

            {loading ? (
              <div className='text-center py-40'>{t("global.loading")}</div>
            ) : profiles.length === 0 ? (
              <div className='alert alert-info mb-0'>{t("perfis.hint.noProfiles")}</div>
            ) : (
              <div className='table-responsive'>
                <table className='table bordered-table mb-0'>
                  <thead>
                    <tr>
                      <th>{t("perfis.col.name")}</th>
                      <th>{t("perfis.col.permissionsCount")}</th>
                      <th>{t("perfis.col.status")}</th>
                      <th>{t("perfis.col.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profiles.map((profile) => (
                      <tr key={profile.id}>
                        <td>
                          {profile.description}
                          {profile.isSystem && (
                            <span className='badge bg-primary-50 text-primary-600 ms-2'>
                              {t("perfis.badge.system")}
                            </span>
                          )}
                        </td>
                        <td>{permissionsLabel(profile)}</td>
                        <td>
                          <span className={`badge ${profile.active === 0 ? "bg-danger-50 text-danger-600" : "bg-success-50 text-success-600"}`}>
                            {profile.active === 0 ? t("global.inactive") : t("global.active")}
                          </span>
                        </td>
                        <td>
                          <div className='d-flex flex-wrap gap-2'>
                            <button type='button' className='btn btn-outline-primary-600 btn-sm d-flex align-items-center gap-1' onClick={() => openEdit(profile)}>
                              <Icon icon='mdi:pencil-outline' />
                              {t("global.edit")}
                            </button>
                            {!profile.isSystem && (
                              <button type='button' className='btn btn-outline-danger-600 btn-sm d-flex align-items-center gap-1' onClick={() => handleDeactivate(profile)}>
                                <Icon icon='mdi:trash-can-outline' />
                                {t("global.delete")}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {editTarget && editForm && (
        <div className='modal show d-block' style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className='modal-dialog modal-lg'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h6 className='mb-0'>{t("perfis.action.edit")}</h6>
                <button type='button' className='btn-close' onClick={closeEdit} />
              </div>
              <div className='modal-body'>
                <div className='row gy-3'>
                  <div className='col-12'>
                    <label className='form-label'>{t("perfis.field.name")}</label>
                    <input
                      type='text'
                      className='form-control'
                      value={editForm.description}
                      onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                    />
                  </div>
                  <div className='col-12'>
                    <label className='form-label'>{t("perfis.field.permissions")}</label>
                    {editTarget.isSystem ? (
                      <div className='alert alert-info mb-0'>{t("perfis.hint.superAdminNote")}</div>
                    ) : (
                      <PermissionGroupsField selectedCodes={editPermissions} onChange={setEditPermissions} />
                    )}
                  </div>
                </div>
              </div>
              <div className='modal-footer'>
                <button type='button' className='btn btn-outline-secondary' onClick={closeEdit}>
                  {t("global.cancel")}
                </button>
                <button type='button' className='btn btn-primary-600' onClick={handleSaveEdit} disabled={saving}>
                  {saving ? t("global.loading") : (<><i className='ri-save-line' /> {t("global.save")}</>)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {ConfirmModal}
    </div>
  );
};

export default ListaPerfisLayer;
